import { API_URLS } from "@/constants";
import apiRequest from "@/lib/axios";
import { AdminSidebar } from "@/types";

// In-flight / resolved promise shared across callers. StrictMode's double
// effect-invoke plus the two parallel consumers (RoleGuard + MenuContent)
// were otherwise causing 4 identical GETs on first load.
let sidebarsPromise: Promise<AdminSidebar[]> | null = null;

export const invalidateAdminSidebarsCache = () => {
  sidebarsPromise = null;
};

export const getAdminSidebars = async (): Promise<AdminSidebar[]> => {
  if (sidebarsPromise) return sidebarsPromise;
  sidebarsPromise = apiRequest<AdminSidebar[]>({
    method: "GET",
    url: API_URLS.ADMIN_SIDEBAR.SIDEBAR,
    errorMessage: "Failed to load the admin sidebar. Please try again.",
  }).catch((error) => {
    // Don't poison the cache on failure - clear it so the next caller can
    // retry. Re-throw so the original caller's error path still fires.
    sidebarsPromise = null;
    throw error;
  });

  return sidebarsPromise;
};

export const createAdminSidebar = async (data: Partial<AdminSidebar>): Promise<AdminSidebar> => {
  const result = await apiRequest<AdminSidebar>({
    method: "POST",
    url: API_URLS.ADMIN_SIDEBAR.SIDEBAR,
    data,
    errorMessage: "Failed to create the admin sidebar. Please try again.",
  });
  invalidateAdminSidebarsCache();

  return result;
};

export const updateAdminSidebar = async (
  id: string,
  data: Partial<AdminSidebar>,
): Promise<AdminSidebar> => {
  const result = await apiRequest<AdminSidebar>({
    method: "PUT",
    url: API_URLS.ADMIN_SIDEBAR.SIDEBAR_BY_ID(id),
    data,
    errorMessage: "Failed to update the admin sidebar. Please try again.",
  });
  invalidateAdminSidebarsCache();

  return result;
};

export const deleteAdminSidebar = async (id: string): Promise<void> => {
  await apiRequest({
    method: "DELETE",
    url: API_URLS.ADMIN_SIDEBAR.SIDEBAR_BY_ID(id),
    errorMessage: "Failed to delete the admin sidebar. Please try again.",
  });
  invalidateAdminSidebarsCache();
};
